import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import ProductVisual from "@/components/ProductVisual";
import { PageHero, SectionHead, CTABand } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { PRODUCTS } from "@/data/site";

export default function Products() {
  const featured = PRODUCTS.find((p) => p.featured);
  const rest = PRODUCTS.filter((p) => !p.featured);
  return (
    <>
      <Seo
        title="Products & Solutions — BNM Sustainable Energy Limited"
        description="The BNM product portfolio: the Carbon Free Generator in development, plus hybrid energy systems, community power solutions and energy storage under evaluation."
      />
      <PageHero
        testid="products-hero"
        eyebrow="Products & Solutions"
        lines={["A portfolio built", "to grow."]}
        lead="BNM's product architecture starts with the Carbon Free Generator and expands into hybrid systems, community power and storage. Products are announced only when verified."
      />

      <section data-testid="products-featured" className="bg-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal>
            <article className="rounded-lg border border-slate-200 bg-white overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="bg-obsidian p-8 lg:p-12 flex items-center">
                  <ProductVisual caption="Flagship — In Development" />
                </div>
                <div className="p-8 lg:p-12">
                  <span className="inline-block rounded-full border border-emerald-300 bg-emerald-50 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-emerald-700">
                    {featured.status}
                  </span>
                  <h2 className="mt-5 font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                    {featured.name}
                  </h2>
                  <p className="mt-4 text-base leading-relaxed text-slate-600">{featured.blurb}</p>
                  <Link
                    to={featured.to}
                    data-testid="products-featured-cta"
                    className="mt-7 inline-flex items-center gap-2 font-semibold text-emerald-700 hover:text-emerald-900 transition-colors"
                  >
                    Explore the flagship
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </article>
          </Reveal>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
            {rest.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.08}>
                <article className="h-full rounded-lg border border-slate-200 bg-white p-8 flex flex-col transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-emerald-300">
                  <span className="inline-flex self-start rounded-full border border-dashed border-slate-300 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500">
                    {p.status}
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold tracking-tight text-slate-900">{p.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{p.blurb}</p>
                  <Link
                    to={p.to}
                    data-testid={`products-card-cta-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:text-emerald-900 transition-colors"
                  >
                    {p.live ? "Open the calculator" : "Register interest"}
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <p className="mt-10 max-w-3xl text-sm leading-relaxed text-slate-500">
              This portfolio is designed to expand. New BNM products are added here as they complete development
              milestones — with verified specifications, never projections presented as fact.
            </p>
          </Reveal>
        </div>
      </section>

      <CTABand
        title="Looking for a specific energy solution?"
        text="Tell us about your application — homes, institutions, agriculture, enterprise or emergency power — and we will advise on the BNM roadmap."
        primary={{ label: "Discuss Your Requirements", to: "/contact", testid: "products-contact-cta" }}
        testid="products-cta"
      />
    </>
  );
}
